'use client';

import { UserDropdownMenu } from '@/partials/topbar/user-dropdown-menu';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/providers/i18n-provider';
import { CreditsPill } from '@/new_components/shared/CreditsPill';
import { NotificationsBell } from '@/new_components/notifications/NotificationsBell';

// `stable` renders the topbar in a fixed desktop config that doesn't change at
// the `lg`/`xl` breakpoints (language toggle always shown, fixed gaps). Both the
// fit-probe and the real desktop row pass it: the row is now shown/hidden by JS
// measurement, not a CSS breakpoint, so any internal breakpoint would (a) make
// the probe's width non-monotonic and flip the header, and (b) make the search
// visibly jump when the topbar collapses mid-range. See header.tsx.
//
// `compactAuth` (only meaningful with `stable`) collapses the logged-out
// Register/Login pills into the single profile icon, so the header can stay in
// desktop mode at narrower widths before falling back to the mobile header.
const HeaderTopbar = ({
  stable = false,
  compactAuth = false,
}: { stable?: boolean; compactAuth?: boolean } = {}) => {
  const { languageCode, changeLanguage } = useLanguage();

  const toggleLanguage = () => {
    const newLanguage = languageCode === 'en' ? 'de' : 'en';
    changeLanguage(newLanguage);
  };

  return (
    <div className={cn('flex items-center shrink-0', stable ? 'gap-2' : 'gap-1 lg:gap-2')}>
      {/* Credit balance + notification bell — ambient awareness next to the
          account entry point. Both render null for guests, so they never
          disturb the fit probe's logged-out measurements (see header.tsx). */}
      <CreditsPill />
      <NotificationsBell />

      {/* User dropdown — placed first so the language toggle ends up at the
          absolute right edge of the header. */}
      <div className="mr-1">
        <UserDropdownMenu stable={stable} compactAuth={compactAuth} />
      </div>

      {/* Language Toggle — pill-style with mono label, echoes the homepage tone.
          Hidden below lg; on mobile the toggle lives in the sidebar instead. */}
      <button
        type="button"
        onClick={toggleLanguage}
        className={cn(
          'items-center rounded-full border h-9 transition-colors',
          stable
            ? 'inline-flex gap-2 px-3'
            : 'hidden lg:inline-flex gap-1.5 lg:gap-2 px-2.5 lg:px-3',
        )}
        style={{
          borderColor: 'var(--fd-line-2)',
          background: 'transparent',
          color: 'var(--fd-ink-2)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'var(--fd-bg-2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
        }}
        aria-label={
          languageCode === 'en' ? 'Switch to German' : 'Zu Englisch wechseln'
        }
      >
        <img
          src={
            languageCode === 'en'
              ? '/media/flags/united-kingdom.svg'
              : '/media/flags/germany.svg'
          }
          alt=""
          className="w-5 h-3.5 rounded-[2px] object-cover"
        />
        <span className="fd-mono text-[11px] font-medium tracking-[0.04em]">
          {languageCode === 'en' ? 'EN' : 'DE'}
        </span>
      </button>
    </div>
  );
};

export { HeaderTopbar };
